import { useCallback, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import Form from "./Form";
import { useMyRequests } from "../api";
import { formatFormData } from "../../TimeClock/ClockIn/helpers";
import { useUser } from "../../../utils/hooks/user";
import { getPrimaryActiveResource } from "../../../utils/helpers/user";
import { useGetIpAddress } from "../../TimeClock/ClockIn/api";
import { OrgLevelGroupsContext } from "../../Settings/General/OrganisationLevelGroups/context";
import { combineDateAndTime } from "../../../utils/helpers/date";
import { useVisible } from "../../../utils/hooks/useVisible";
import "./_style.scss";

function getShiftDurationHours(values) {
	let start = combineDateAndTime(values.start?.date, values.start?.time);
	let end = combineDateAndTime(values.end?.date, values.end?.time);
	return end.diff(start, "hours", true);
}

function AddShift({ isOpen, onClose, setData }) {
	const { t } = useTranslation();
	const { groups } = useContext(OrgLevelGroupsContext);
	const { visible: warningModalOpen, selected: pendingValues, open: openWarningModal, close: closeWarningModal } = useVisible();

	const useInClockInGroup = useMemo(
		() => groups?.find(({ useInClockIn }) => useInClockIn),
		[groups],
	);

	const user = useUser();

	const { create, loading, error } = useMyRequests();

	const { ip, getIp } = useGetIpAddress();

	const job = useMemo(
		() => getPrimaryActiveResource(user?.jobs || []),
		[user?.jobs],
	);

	const location = useMemo(
		() => getPrimaryActiveResource(user?.locations || []),
		[user?.locations],
	);

	const values = useMemo(
		() => ({
			data: {
				location,
				job,
			},
		}),
		[location, job],
	);

	const submitRequest = useCallback(
		(formValues) => {
			if (!user) return;

			const jobResource = user.jobs?.find(({ id }) => id === formValues.job);
			const locationResource = user.locations?.find(
				({ id }) => id === formValues.location,
			);

			const request = {
				user: user.id,
				type: "shift",
				data: formatFormData({
					...formValues,
					job: jobResource,
					location: locationResource,
					project: {
						id: formValues?.project?.id,
						code: formValues?.project?.code,
						description: formValues?.project?.description,
						glSegment: formValues?.project?.glSegment,
						orgLevelGroup: formValues?.project?.orgLevelGroup,
						level: useInClockInGroup?.level?.replace("_", ""),
					},
					ip: ip ? ip : "",
				}),
				device: "web-application",
				ip: ip ? ip : "",
				namespace: "clock"
			};

			create(request, (request) => {
				closeWarningModal();
				onClose();
				setData((prev) => prev.concat(request));
				toast.success(t("request-created-successfully"));
			});
		},
		[t, user, create, setData, onClose, ip, useInClockInGroup, closeWarningModal],
	);

	const onFinish = useCallback(
		(formValues) => {
			if (!user) return;

			const durationHours = getShiftDurationHours(formValues);

			if (durationHours > 24) {
				openWarningModal(formValues);
				return;
			}

			submitRequest(formValues);
		},
		[user, submitRequest, openWarningModal],
	);

	const onConfirmWarning = useCallback(() => {
		if (pendingValues) {
			submitRequest(pendingValues);
		}
	}, [pendingValues, submitRequest]);

	const onCloseAddShift = useCallback(() => {
		closeWarningModal();
		onClose();
	}, [closeWarningModal, onClose]);

	useEffect(() => {
		const controller = new AbortController();
		getIp(controller);
		return () => controller.abort();
	}, [getIp]);

	useEffect(() => {
		if (!isOpen) {
			closeWarningModal();
		}
	}, [isOpen, closeWarningModal]);

	return (
		<>
			<Modal
				className="add-shift-modal modal-dialog-centered modal-lg pb-0 shadow-none"
				title={t("add-shift-request")}
				isOpen={isOpen}
				toggle={onCloseAddShift}
				size="lg"
				scrollable
			>
				<Form
					onClose={onCloseAddShift}
					values={values}
					onFinish={onFinish}
					error={error}
					loading={loading}
				/>
			</Modal>

			<Modal
				isOpen={warningModalOpen}
				toggle={closeWarningModal}
				title={
					<span className="d-flex align-items-center text-warning">
						<FontAwesomeIcon icon={faTriangleExclamation} className="mr-2" />
						{t("shift-request-over-24h-warning-title")}
					</span>
				}
				className="shift-request-warning-modal"
				centered
				footer={
					<>
						<Button color="secondary" onClick={closeWarningModal}>
							{t("cancel")}
						</Button>
						<Button color="warning" onClick={onConfirmWarning} disabled={loading}>
							{t("confirm")}
						</Button>
					</>
				}
			>
				<div className="d-flex align-items-start bg-warning-soft">
					<p className="mb-0 text-warning font-weight-bold">{t("shift-request-over-24h-warning-message")}</p>
				</div>
			</Modal>
		</>
	);
}

export default AddShift;
